import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * <audio-sample>
 * Inline audio sample: trigger button transforms into a player on click
 * (pause = collapse back to trigger, scrubbable progress bar, "Ns left"
 * countdown). On variant:update shows itself iff the selected variant ID
 * is in the audiobook set, and hides any sibling sample-PDF block while
 * audio is active.
 */
class AudioSample extends Component {
  /** @type {Set<string> | null} */
  #ids = null;
  /** @type {Element | null} */
  #section = null;
  /** @type {number | null} */
  #tooltipTimer = null;
  /** @type {boolean} */
  #wasVisible = false;
  /** @type {boolean} */
  #dragging = false;

  connectedCallback() {
    super.connectedCallback();
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);

    const audio = this.#audio();
    const v = parseFloat(this.dataset.volume ?? '0.5');
    if (audio && !Number.isNaN(v)) audio.volume = Math.min(1, Math.max(0, v));

    this.querySelector('[data-audio-trigger]')?.addEventListener('click', this.#startPlayback);
    this.querySelector('[data-playpause]')?.addEventListener('click', this.#collapseToTrigger);
    const progress = this.querySelector('[data-progress]');
    progress?.addEventListener('pointerdown', this.#onPointerDown);
    progress?.addEventListener('keydown', this.#onKeyDown);

    audio?.addEventListener('timeupdate', this.#onTimeUpdate);
    audio?.addEventListener('loadedmetadata', this.#onTimeUpdate);
    audio?.addEventListener('durationchange', this.#onTimeUpdate);
    audio?.addEventListener('ended', () => {
      window.publishStorefrontEvent?.('audiobook_sample_completed', {
        product_id: this.dataset.productId || null,
        media_type: 'audio',
      });
    });
    audio?.addEventListener('ended', this.#collapseToTrigger);

    this.#wasVisible = !this.hasAttribute('hidden');
    this.#applyVisibility(this.#wasVisible);
    // Show the tooltip if the audiobook variant is already selected on load.
    if (this.#wasVisible) this.#showTooltip();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#section?.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    if (this.#tooltipTimer) clearTimeout(this.#tooltipTimer);
  }

  #handleVariantUpdate = (event) => {
    const variant = event.detail?.resource;
    if (!variant) return;
    const isAudiobook = this.#getIds().has(String(variant.id));
    if (!isAudiobook) {
      this.#resetToTrigger();
      this.#hideTooltip();
    }
    this.#applyVisibility(isAudiobook);
    // Only pop the tooltip on the transition into the audiobook variant, so it
    // doesn't re-fire when other options change while still on audiobook.
    if (isAudiobook && !this.#wasVisible) this.#showTooltip();
    this.#wasVisible = isAudiobook;
  };

  #showTooltip() {
    const tip = this.querySelector('[data-tooltip]');
    if (!tip) return;
    const ms = parseInt(this.dataset.tooltipDuration ?? '', 10);
    const duration = Number.isFinite(ms) && ms > 0 ? ms : 5000;
    if (this.#tooltipTimer) clearTimeout(this.#tooltipTimer);
    tip.removeAttribute('hidden');
    this.#tooltipTimer = setTimeout(() => {
      tip.setAttribute('hidden', '');
      this.#tooltipTimer = null;
    }, duration);
  }

  #hideTooltip() {
    if (this.#tooltipTimer) {
      clearTimeout(this.#tooltipTimer);
      this.#tooltipTimer = null;
    }
    this.querySelector('[data-tooltip]')?.setAttribute('hidden', '');
  }

  #startPlayback = () => {
    this.#hideTooltip();
    this.#showPlayer();
    this.#audio()
      ?.play()
      .catch(() => {
        // Gesture-initiated play() rejecting = the sample genuinely failed to
        // start (bad file, network), not an autoplay-policy block.
        window.publishStorefrontEvent?.('media_playback_failed', {
          media_type: 'audio',
          component: 'audio_sample',
          product_id: this.dataset.productId || null,
          error_type: 'unknown',
        });
      });
    window.publishStorefrontEvent?.('audiobook_sample_started', {
      product_id: this.dataset.productId || null,
      media_type: 'audio',
    });
  };

  // Tap / click-or-drag scrubbing. Pointer Events cover mouse, touch and pen
  // with one code path; pointer capture keeps move/up events flowing to the
  // track even when the finger or cursor slides off it mid-drag.
  #onPointerDown = (event) => {
    // Ignore secondary mouse buttons; touch/pen report button 0.
    if (event.button != null && event.button > 0) return;
    const track = event.currentTarget;
    this.#dragging = true;
    try {
      track.setPointerCapture(event.pointerId);
    } catch {}
    track.addEventListener('pointermove', this.#onPointerMove);
    track.addEventListener('pointerup', this.#onPointerUp);
    track.addEventListener('pointercancel', this.#onPointerUp);
    // Stop the touch from scrolling the page while scrubbing.
    event.preventDefault();
    this.#seekToClientX(event.clientX, track);
  };

  #onPointerMove = (event) => {
    if (!this.#dragging) return;
    event.preventDefault();
    this.#seekToClientX(event.clientX, event.currentTarget);
  };

  #onPointerUp = (event) => {
    if (!this.#dragging) return;
    this.#dragging = false;
    const track = event.currentTarget;
    try {
      track.releasePointerCapture(event.pointerId);
    } catch {}
    track.removeEventListener('pointermove', this.#onPointerMove);
    track.removeEventListener('pointerup', this.#onPointerUp);
    track.removeEventListener('pointercancel', this.#onPointerUp);
  };

  #seekToClientX = (clientX, track) => {
    const audio = this.#audio();
    if (!audio || !track || !Number.isFinite(audio.duration) || audio.duration <= 0) return;
    const rect = track.getBoundingClientRect();
    if (rect.width <= 0) return;
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    audio.currentTime = ratio * audio.duration;
    // Jump the playhead to the pointer position instantly instead of letting the
    // fill transition animate to it, so scrubbing feels snappy.
    const bar = this.querySelector('[data-progress-bar]');
    if (bar) {
      const prev = bar.style.transition;
      bar.style.transition = 'none';
      this.#onTimeUpdate();
      void bar.offsetWidth; // commit the un-transitioned width before restoring
      bar.style.transition = prev;
    } else {
      this.#onTimeUpdate();
    }
  };

  // Keyboard seeking on the slider track: arrows ±5s, Home/End to the bounds.
  #onKeyDown = (event) => {
    const audio = this.#audio();
    if (!audio || !Number.isFinite(audio.duration) || audio.duration <= 0) return;
    let next = null;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowUp':
        next = audio.currentTime + 5;
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        next = audio.currentTime - 5;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = audio.duration;
        break;
      default:
        return;
    }
    event.preventDefault();
    audio.currentTime = Math.min(audio.duration, Math.max(0, next));
    this.#onTimeUpdate();
  };

  #onTimeUpdate = () => {
    const audio = this.#audio();
    if (!audio || !Number.isFinite(audio.duration) || audio.duration <= 0) return;
    const ratio = audio.currentTime / audio.duration;
    const bar = this.querySelector('[data-progress-bar]');
    const countdown = this.querySelector('[data-countdown]');
    if (bar) bar.style.width = `${Math.min(100, ratio * 100)}%`;
    const remaining = Math.max(0, Math.ceil(audio.duration - audio.currentTime));
    const minutes = Math.floor(remaining / 60);
    const seconds = remaining % 60;
    const label = minutes > 0 ? `${minutes}m ${String(seconds).padStart(2, '0')}s` : `${seconds}s`;
    if (countdown) countdown.textContent = `${label} left`;
    const track = this.querySelector('[data-progress]');
    if (track) {
      track.setAttribute('aria-valuenow', String(Math.round(Math.min(100, ratio * 100))));
      track.setAttribute('aria-valuetext', `${label} left`);
    }
  };

  #showPlayer() {
    this.querySelector('[data-audio-trigger]')?.setAttribute('hidden', '');
    this.querySelector('[data-player]')?.removeAttribute('hidden');
    this.#onTimeUpdate();
  }

  // Pause / end: shrink the track closed (mirror of the open) then swap back
  // to the trigger. Falls back to an instant reset when there's nothing to
  // animate (already hidden, mid-collapse, or reduced-motion).
  #collapseToTrigger = () => {
    const audio = this.#audio();
    // A collapse while audio is mid-play is a shopper pause; the natural end
    // of the sample publishes audiobook_sample_completed from the 'ended'
    // listener instead.
    if (audio && !audio.ended && !audio.paused && audio.currentTime > 0 && audio.duration > 0) {
      window.publishStorefrontEvent?.('audiobook_sample_paused', {
        product_id: this.dataset.productId || null,
        media_type: 'audio',
        progress_pct: Math.round((audio.currentTime / audio.duration) * 100),
      });
    }
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    const player = this.querySelector('[data-player]');
    const track = player?.querySelector('[data-progress]');
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!player || !track || player.hasAttribute('hidden') || player.hasAttribute('data-collapsing') || reduce) {
      this.#resetToTrigger();
      return;
    }
    player.setAttribute('data-collapsing', '');
    const onEnd = (event) => {
      if (event.animationName !== 'audio-sample-collapse') return;
      track.removeEventListener('animationend', onEnd);
      this.#resetToTrigger();
    };
    track.addEventListener('animationend', onEnd);
  };

  #resetToTrigger = () => {
    const audio = this.#audio();
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    const player = this.querySelector('[data-player]');
    player?.removeAttribute('data-collapsing');
    player?.setAttribute('hidden', '');
    this.querySelector('[data-audio-trigger]')?.removeAttribute('hidden');
    const bar = this.querySelector('[data-progress-bar]');
    const countdown = this.querySelector('[data-countdown]');
    if (bar) bar.style.width = '0%';
    if (countdown) countdown.textContent = '';
    const track = this.querySelector('[data-progress]');
    if (track) {
      track.setAttribute('aria-valuenow', '0');
      track.removeAttribute('aria-valuetext');
    }
  };

  #applyVisibility(visible) {
    if (visible) this.removeAttribute('hidden');
    else this.setAttribute('hidden', '');
    const pdfBlocks = this.#section?.querySelectorAll('[data-sample-pdf]') ?? [];
    pdfBlocks.forEach((el) => {
      if (visible) el.setAttribute('hidden', '');
      else el.removeAttribute('hidden');
    });
  }

  #audio() {
    return /** @type {HTMLAudioElement | null} */ (this.querySelector('[data-audio]'));
  }

  #getIds() {
    if (this.#ids) return this.#ids;
    const raw = this.dataset.audiobookVariantIds || '';
    this.#ids = new Set(raw.split(',').map((s) => s.trim()).filter(Boolean));
    return this.#ids;
  }
}

if (!customElements.get('audio-sample')) {
  customElements.define('audio-sample', AudioSample);
}
